/**
 * − 7 +: how many persons a tiffin is for, and for how many days.
 *
 * Deliberately not the donation's headcount tiles. A tiffin is for a
 * household, not a crowd, and for a week or a month, not a day at a time, so
 * the control counts in ones between the kitchen's limits and there are no
 * bulk slabs to skip past.
 */

import { memo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { colors, radius, space } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

const NO_OUTLINE = { outlineStyle: 'none' } as unknown as ViewStyle;
const ROUND = 52;

export interface CountStepperProps {
  value: number;
  /** One unless the kitchen says otherwise. */
  min?: number;
  max: number;
  onChange: (value: number) => void;
  /** The word under the number, for one and for more: `['Person', 'Persons']`. */
  unit: readonly [string, string];
  /** What − and + do, for a screen reader: `['Fewer persons', 'More persons']`. */
  actions: readonly [string, string];
  testID: string;
}

export const CountStepper = memo(function CountStepper({
  value,
  min = 1,
  max,
  onChange,
  unit,
  actions,
  testID,
}: CountStepperProps) {
  const t = useT();
  const canDecrease = value > min;
  const canIncrease = value < max;
  const word = t(value === 1 ? unit[0] : unit[1]);

  return (
    <View style={styles.row} testID={testID}>
      <Pressable
        testID={`${testID}-decrease`}
        onPress={() => onChange(value - 1)}
        disabled={!canDecrease}
        accessibilityRole="button"
        accessibilityLabel={t(actions[0])}
        accessibilityHint={!canDecrease ? `${t('At least')} ${min}` : undefined}
        style={({ pressed }) => [
          styles.round,
          !canDecrease && styles.roundDisabled,
          pressed && styles.pressed,
          NO_OUTLINE,
        ]}
      >
        <Text variant="screenTitle" tone={canDecrease ? 'brand' : 'disabled'}>−</Text>
      </Pressable>

      <View
        style={styles.value}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
        accessibilityLabel={`${value} ${word}`}
      >
        <Text variant="hero" tone="brand" align="center" testID={`${testID}-value`}>
          {value}
        </Text>
        <Text variant="label" tone="secondary" align="center">
          {word}
        </Text>
      </View>

      <Pressable
        testID={`${testID}-increase`}
        onPress={() => onChange(value + 1)}
        disabled={!canIncrease}
        accessibilityRole="button"
        accessibilityLabel={t(actions[1])}
        accessibilityHint={!canIncrease ? `${t('Up to')} ${max}` : undefined}
        style={({ pressed }) => [
          styles.round,
          !canIncrease && styles.roundDisabled,
          pressed && styles.pressed,
          NO_OUTLINE,
        ]}
      >
        <Text variant="screenTitle" tone={canIncrease ? 'brand' : 'disabled'}>+</Text>
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.md,
  },
  round: {
    width: ROUND,
    height: ROUND,
    borderRadius: radius.full,
    borderWidth: 2,
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.brandSubtle,
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundDisabled: {
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surfaceSunken,
  },
  pressed: {
    opacity: 0.7,
  },
  value: {
    flex: 1,
    alignItems: 'center',
  },
});
